import { supabase } from '../lib/supabase'

export async function fetchGames(status) {
  let query = supabase
    .from('games')
    .select(`
      *,
      game_players (
        *,
        player:players (*)
      )
    `)
    .order('created_at', { ascending: false })

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query
  if (error) throw error
  return data
}

export async function fetchGame(id) {
  const { data, error } = await supabase
    .from('games')
    .select(`
      *,
      game_players (
        *,
        player:players (*)
      ),
      scores (*),
      teams (
        *,
        team_players (
          *,
          player:players (*)
        )
      ),
      bets (
        *,
        bet_participants (
          *,
          player:players (*)
        )
      ),
      ctp_results (*)
    `)
    .eq('id', id)
    .single()

  if (error) throw error
  return data
}

export async function fetchMatchGames(matchId) {
  const { data, error } = await supabase
    .from('games')
    .select(`
      *,
      game_players (
        *,
        player:players (*)
      ),
      scores (*),
      teams (
        *,
        team_players (
          *,
          player:players (*)
        )
      ),
      bets (
        *,
        bet_participants (
          *,
          player:players (*)
        )
      ),
      ctp_results (*)
    `)
    .eq('match_id', matchId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return data
}

export async function createGame({ game_name, game_type, game_subtype, game_config, course_name, course_id, tee_name, wager, num_holes, match_id, player_ids, teams }) {
  const { data: { user } } = await supabase.auth.getUser()

  // Prevent starting a new round while one is already active
  if (match_id) {
    const { data: activeGames } = await supabase
      .from('games')
      .select('id')
      .eq('match_id', match_id)
      .eq('status', 'in_progress')
      .limit(1)

    if (activeGames && activeGames.length > 0) {
      throw new Error('Cannot start a new round while another round is still active. Finish the current round first.')
    }
  }

  // Create game
  const { data: game, error: gameError } = await supabase
    .from('games')
    .insert({
      created_by: user.id,
      match_id: match_id || null,
      game_name: game_name || null,
      game_type: game_type || 'stroke',
      game_subtype: game_subtype || null,
      game_config: game_config || {},
      course_name: course_name || null,
      course_id: course_id || null,
      tee_name: tee_name || null,
      wager: wager || 0,
      num_holes,
      status: 'in_progress',
      started_at: new Date().toISOString(),
    })
    .select()
    .single()

  if (gameError) throw gameError

  // Add players to game
  if (player_ids?.length > 0) {
    const gamePlayers = player_ids.map((player_id, index) => ({
      game_id: game.id,
      player_id,
      position: index + 1,
    }))

    const { error: playersError } = await supabase
      .from('game_players')
      .insert(gamePlayers)

    if (playersError) throw playersError
  }

  // Create teams if provided
  if (teams?.length > 0) {
    for (let i = 0; i < teams.length; i++) {
      const team = teams[i]
      const { data: teamRow, error: teamError } = await supabase
        .from('teams')
        .insert({
          game_id: game.id,
          team_name: team.name,
          team_order: i,
        })
        .select()
        .single()

      if (teamError) throw teamError

      if (team.playerIds?.length > 0) {
        const teamPlayers = team.playerIds.map((player_id) => ({
          team_id: teamRow.id,
          player_id,
        }))

        const { error: tpError } = await supabase
          .from('team_players')
          .insert(teamPlayers)

        if (tpError) throw tpError
      }
    }
  }

  return game
}

export async function updateRoundSettings(gameId, { game_name, wager, teams }) {
  // Update game-level fields
  const gameUpdates = {}
  if (game_name !== undefined) gameUpdates.game_name = game_name || null
  if (wager !== undefined) gameUpdates.wager = wager

  if (Object.keys(gameUpdates).length > 0) {
    const { error } = await supabase
      .from('games')
      .update(gameUpdates)
      .eq('id', gameId)
    if (error) throw error
  }

  // Rebuild teams if provided
  if (teams) {
    // Get existing team IDs for this game
    const { data: existingTeams } = await supabase
      .from('teams')
      .select('id')
      .eq('game_id', gameId)

    if (existingTeams?.length > 0) {
      const teamIds = existingTeams.map((t) => t.id)
      // Delete team_players first (FK constraint)
      const { error: tpErr } = await supabase
        .from('team_players')
        .delete()
        .in('team_id', teamIds)
      if (tpErr) throw tpErr

      // Delete teams
      const { error: tErr } = await supabase
        .from('teams')
        .delete()
        .eq('game_id', gameId)
      if (tErr) throw tErr
    }

    // Create new teams
    for (let i = 0; i < teams.length; i++) {
      const team = teams[i]
      const { data: teamRow, error: teamError } = await supabase
        .from('teams')
        .insert({
          game_id: gameId,
          team_name: team.name,
          team_order: i,
        })
        .select()
        .single()

      if (teamError) throw teamError

      if (team.playerIds?.length > 0) {
        const teamPlayers = team.playerIds.map((player_id) => ({
          team_id: teamRow.id,
          player_id,
        }))
        const { error: tpError } = await supabase
          .from('team_players')
          .insert(teamPlayers)
        if (tpError) throw tpError
      }
    }
  }

  // Return updated game
  return fetchGame(gameId)
}

export async function updateGame(id, updates) {
  const { data, error } = await supabase
    .from('games')
    .update(updates)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// Presses
// ============================================

export async function fetchPresses(gameId) {
  const { data, error } = await supabase
    .from('bets')
    .select('*')
    .eq('game_id', gameId)
    .eq('bet_type', 'press')
    .order('created_at', { ascending: true })

  if (error) throw error
  return data
}

export async function createPress({ game_id, segment, start_hole, end_hole, wager, pressing_team, parent_press_id, trigger_type }) {
  const metadata = { segment, pressing_team }
  if (parent_press_id) metadata.parent_press_id = parent_press_id
  if (trigger_type) metadata.trigger = trigger_type
  metadata.declared_at_hole = start_hole

  const description = parent_press_id
    ? `Re-press on ${segment} from hole ${start_hole}`
    : `Press on ${segment} from hole ${start_hole}`

  const { data, error } = await supabase
    .from('bets')
    .insert({
      game_id,
      bet_type: 'press',
      description,
      amount: wager,
      hole_start: start_hole,
      hole_end: end_hole,
      status: 'active',
      metadata,
    })
    .select()
    .single()

  if (error) throw error
  return data
}

export async function deletePress(pressId) {
  const { error } = await supabase
    .from('bets')
    .delete()
    .eq('id', pressId)

  if (error) throw error
}

// ============================================
// Settlements generation
// ============================================

export async function createSettlements(gameId, settlements) {
  if (!settlements || settlements.length === 0) return []

  const rows = settlements.map((s) => ({
    game_id: gameId,
    payer_id: s.payerId,
    payee_id: s.payeeId,
    amount: s.amount,
    settled: false,
  }))

  const { data, error } = await supabase
    .from('settlements')
    .insert(rows)
    .select()

  if (error) throw error
  return data
}

export async function deleteGameSettlements(gameId) {
  const { error } = await supabase
    .from('settlements')
    .delete()
    .eq('game_id', gameId)

  if (error) throw error
}

export async function deleteGame(gameId) {
  // Delete settlements first, then the game itself
  await deleteGameSettlements(gameId)

  const { error } = await supabase
    .from('games')
    .delete()
    .eq('id', gameId)

  if (error) throw error
}

export async function resumeGame(gameId) {
  // Check if there's already an active round in this match
  const { data: thisGame } = await supabase
    .from('games')
    .select('match_id')
    .eq('id', gameId)
    .single()

  if (thisGame?.match_id) {
    const { data: activeGames } = await supabase
      .from('games')
      .select('id')
      .eq('match_id', thisGame.match_id)
      .eq('status', 'in_progress')
      .limit(1)

    if (activeGames && activeGames.length > 0) {
      throw new Error('Cannot resume this round while another round is still active. Finish the current round first.')
    }
  }

  // Delete any auto-generated settlements for this round
  const { error: settleError } = await supabase
    .from('settlements')
    .delete()
    .eq('game_id', gameId)

  if (settleError) throw settleError

  // Set game back to in_progress
  const { data, error } = await supabase
    .from('games')
    .update({ status: 'in_progress', completed_at: null, final_results: null })
    .eq('id', gameId)
    .select()
    .single()

  if (error) throw error
  return data
}

// ============================================
// Scores
// ============================================

export async function updateScoreTaken({ game_id, player_id, hole_number, taken }) {
  const { data, error } = await supabase
    .from('scores')
    .update({
      score_taken: taken,
      updated_at: new Date().toISOString(),
    })
    .eq('game_id', game_id)
    .eq('player_id', player_id)
    .eq('hole_number', hole_number)
    .select()
    .single()

  if (error) throw error
  return data
}

export async function fetchScoringAverage(playerId) {
  // Fetch completed 18-hole games where this player participated, with their scores
  const { data: games, error } = await supabase
    .from('games')
    .select(`
      id,
      num_holes,
      game_players!inner (player_id),
      scores (player_id, strokes)
    `)
    .eq('num_holes', 18)
    .eq('status', 'completed')
    .eq('game_players.player_id', playerId)

  if (error) throw error
  if (!games || games.length === 0) return null

  let totalStrokes = 0
  let validRounds = 0

  for (const game of games) {
    const playerScores = (game.scores || []).filter(
      (s) => s.player_id === playerId && s.strokes != null
    )
    // Only count rounds where all 18 holes were scored
    if (playerScores.length === 18) {
      totalStrokes += playerScores.reduce((sum, s) => sum + s.strokes, 0)
      validRounds++
    }
  }

  if (validRounds === 0) return null
  return { average: (totalStrokes / validRounds).toFixed(1), rounds: validRounds }
}

export async function fetchSegmentWinRate(playerId) {
  // Fetch completed games with final_results snapshots where this player participated
  const { data: games, error } = await supabase
    .from('games')
    .select(`
      id,
      game_type,
      final_results,
      game_players!inner (player_id)
    `)
    .eq('status', 'completed')
    .not('final_results', 'is', null)
    .eq('game_players.player_id', playerId)

  if (error) throw error
  if (!games || games.length === 0) return null

  let segmentsWon = 0
  let segmentsPlayed = 0

  for (const game of games) {
    const snap = game.final_results
    if (!snap?.roundResults || !snap?.teamStructure) continue

    const rr = snap.roundResults
    const teams = snap.teamStructure

    // Find which team index this player is on
    const teamIndex = teams.findIndex((t) =>
      t.playerIds?.includes(playerId)
    )
    if (teamIndex === -1) continue

    if (rr.format === 'nassau' && rr.nassau) {
      for (const seg of ['front', 'back', 'overall']) {
        const segData = rr.nassau[seg]
        if (!segData || !segData.complete) continue
        segmentsPlayed++
        if (segData.winner === teamIndex) segmentsWon++
      }
    } else if (rr.format === 'stableford' && rr.stableford) {
      for (const seg of ['front', 'back', 'overall']) {
        const segData = rr.stableford[seg]
        if (!segData || !segData.complete) continue
        segmentsPlayed++
        if (segData.winner === teamIndex) segmentsWon++
      }
    } else if (rr.format === 'six_pack' && rr.six_pack) {
      for (const seg of ['front', 'back', 'overall']) {
        const segData = rr.six_pack[seg]
        if (!segData || !segData.complete) continue
        segmentsPlayed++
        if (segData.winner === teamIndex) segmentsWon++
      }
    } else if (rr.format === 'per_nine' && rr.nineResults) {
      // Hero (per_nine) format: front and back nines only
      for (const nine of rr.nineResults) {
        segmentsPlayed++
        if (nine.winner === teamIndex) segmentsWon++
      }
    }
  }

  if (segmentsPlayed === 0) return null
  return {
    rate: Math.round((segmentsWon / segmentsPlayed) * 100),
    won: segmentsWon,
    played: segmentsPlayed,
  }
}

export async function deleteScore({ game_id, player_id, hole_number }) {
  const { error } = await supabase
    .from('scores')
    .delete()
    .eq('game_id', game_id)
    .eq('player_id', player_id)
    .eq('hole_number', hole_number)

  if (error) throw error
}

export async function upsertScore({ game_id, player_id, hole_number, strokes }) {
  const { data, error } = await supabase
    .from('scores')
    .upsert(
      {
        game_id,
        player_id,
        hole_number,
        strokes,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'game_id,player_id,hole_number' }
    )
    .select()
    .single()

  if (error) throw error
  return data
}
